import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Container, Typography, Button, Box } from '@mui/material';
import { Link } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import Dashboard from './pages/Dashboard';
import Bills from './pages/Bills/Bills';
import BillDetail from './pages/Bills/BillDetail';
import CompareBills from './pages/Bills/CompareBills';
import Members from './pages/Members/Members';
import MemberDetail from './pages/Members/MemberDetail';
import CompareMembers from './pages/Members/CompareMembers';
import Chat from './pages/Chat/Chat';
import Committees from './pages/Committees/Committees';
import CommitteeDetail from './pages/Committees/CommitteeDetail';
import Nominations from './pages/Nominations/Nominations';
import NominationDetail from './pages/Nominations/NominationDetail';
import Login from './pages/Login';
import LoadingSpinner from './components/Common/LoadingSpinner';
import AdminDashboard from './pages/Admin/AdminDashboard';
import SystemStatus from './pages/Admin/SystemStatus';
import MemberSocialAdmin from './pages/Admin/MemberSocialAdmin';
import OllamaEndpointsAdmin from './pages/Admin/OllamaEndpointsAdmin';
import AdsenseAdmin from './pages/Admin/AdsenseAdmin';
import CeleryQueuesAdmin from './pages/Admin/CeleryQueuesAdmin';
import SupportMessagesAdmin from './pages/Admin/SupportMessagesAdmin';
import IssueDetail from './pages/Issues/IssueDetail';
import Widgets from './pages/Integrations/Widgets';
import CivicResponseStudio from './pages/CivicResponseStudio';
import AdminCivicResponse from './pages/Admin/AdminCivicResponse';
import Tags from './pages/Issues/Tags';
import TagDetail from './pages/Issues/TagDetail';
import SyncInspector from './pages/Admin/SyncInspector';
import Congresses from './pages/Congress/Congresses';
import CongressDetail from './pages/Congress/CongressDetail';
import Notifications from './pages/Notifications';
import Bookmarks from './pages/Bookmarks';
import Profile from './pages/Profile';
import MyProfile from './pages/MyProfile';
import SavedSearches from './pages/SavedSearches';
import Collections from './pages/Collections';
import Alerts from './pages/Alerts';
import Notes from './pages/Notes';
import VoteAlignmentQuiz from './pages/Me/VoteAlignmentQuiz';
import About from './pages/About';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import Sitemap from './pages/Sitemap';

function App() {
  const { authenticated, initialized } = useAuth();

  // Protect only sensitive routes; keep public pages accessible for SEO/verification
  const RequireAuth: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    // Show a brief spinner until auth state initializes
    if (!initialized) return <LoadingSpinner />;
    if (!authenticated) return <Login />;
    return children;
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Routes>
          {/* Public routes (no auth) */}
          <Route path="/" element={<Dashboard />} />
          <Route path="/bills" element={<Bills />} />
          <Route path="/bills/:congress/:billType/:billNumber" element={<BillDetail />} />
          <Route path="/bill/:congress/:billType/:billNumber" element={<BillDetail />} />
          <Route path="/compare" element={<CompareBills />} />
          <Route path="/compare/bills" element={<CompareBills />} />
          <Route path="/compare/members" element={<CompareMembers />} />
          <Route path="/members" element={<Members />} />
          <Route path="/members/:bioguideId" element={<MemberDetail />} />
          <Route path="/committees" element={<Committees />} />
          <Route path="/committees/:chamber/:committeeCode" element={<CommitteeDetail />} />
          <Route path="/nominations" element={<Nominations />} />
          <Route path="/nominations/:congress/:nominationNumber" element={<NominationDetail />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/issues/:slug" element={<IssueDetail />} />
          <Route path="/tags" element={<Tags />} />
          <Route path="/tags/:slug" element={<TagDetail />} />
          <Route path="/widgets" element={<Widgets />} />
          <Route path="/civic-response-studio" element={<CivicResponseStudio />} />
          <Route path="/u/:handle" element={<Profile />} />
          <Route path="/congress" element={<Congresses />} />
          <Route path="/congress/:number" element={<CongressDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/sitemap" element={<Sitemap />} />

          {/* Protected routes (auth required) */}
          <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
          <Route path="/bookmarks" element={<RequireAuth><Bookmarks /></RequireAuth>} />
          <Route path="/me/profile" element={<RequireAuth><MyProfile /></RequireAuth>} />
          <Route path="/me/saved-searches" element={<RequireAuth><SavedSearches /></RequireAuth>} />
          <Route path="/me/collections" element={<RequireAuth><Collections /></RequireAuth>} />
          <Route path="/me/alerts" element={<RequireAuth><Alerts /></RequireAuth>} />
          <Route path="/me/notes" element={<RequireAuth><Notes /></RequireAuth>} />
          <Route path="/me/quiz/vote-alignment" element={<RequireAuth><VoteAlignmentQuiz /></RequireAuth>} />

          {/* Admin-only sections behind auth (the components themselves also check admin) */}
          <Route path="/admin" element={<RequireAuth><AdminDashboard /></RequireAuth>} />
          <Route path="/admin/system" element={<RequireAuth><SystemStatus /></RequireAuth>} />
          <Route path="/admin/social-handles" element={<RequireAuth><MemberSocialAdmin /></RequireAuth>} />
          <Route path="/admin/ollama" element={<RequireAuth><OllamaEndpointsAdmin /></RequireAuth>} />
          <Route path="/admin/adsense" element={<RequireAuth><AdsenseAdmin /></RequireAuth>} />
          <Route path="/admin/sync-inspector" element={<RequireAuth><SyncInspector /></RequireAuth>} />
          <Route path="/admin/celery" element={<RequireAuth><CeleryQueuesAdmin /></RequireAuth>} />
          <Route path="/admin/support" element={<RequireAuth><SupportMessagesAdmin /></RequireAuth>} />
          <Route path="/admin/civic-response" element={<RequireAuth><AdminCivicResponse /></RequireAuth>} />
          <Route path="*" element={<Box><Typography variant="h4" component="h1">Page not found</Typography><Typography>The page may have moved or the address may be incorrect.</Typography><Button component={Link} to="/">Back to dashboard</Button></Box>} />
        </Routes>
      </Container>
    </Layout>
  );
}

export default App;
